import React, { useEffect, useState, useCallback } from 'react';
import './App.css';

type Theme = 'light' | 'dark';

function App() {
  const [isVisible, setIsVisible] = useState(false);
  const [theme, setTheme] = useState<Theme>(() => {
    // Check localStorage first, then system preference
    const stored = localStorage.getItem('woleh-theme') as Theme | null;
    if (stored) return stored;
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
    return 'dark';
  });

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    // Apply theme to document
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('woleh-theme', theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  const services = [
    {
      title: "Custom Software Development",
      description: "Bespoke solutions engineered to transform your unique business challenges into competitive advantages.",
      icon: "◈"
    },
    {
      title: "Web Application Development",
      description: "Crafting dynamic, responsive applications that deliver seamless digital experiences.",
      icon: "◇"
    },
    {
      title: "Mobile Application Development",
      description: "Native iOS and Android applications that captivate users and drive engagement.",
      icon: "△"
    },
    {
      title: "Cloud Solutions & DevOps",
      description: "Scalable infrastructure and refined development pipelines for modern enterprises.",
      icon: "○"
    },
    {
      title: "UI/UX Design",
      description: "Intuitive interfaces where form meets function in perfect harmony.",
      icon: "□"
    },
    {
      title: "Agentic AI & LLM Solutions",
      description: "Intelligent autonomous agents and large language model integrations that redefine what's possible.",
      icon: "✧"
    }
  ];

  return (
    <div className={`app ${isVisible ? 'visible' : ''}`}>
      {/* Ambient background effects */}
      <div className="ambient-glow ambient-glow-1" />
      <div className="ambient-glow ambient-glow-2" />
      <div className="noise-overlay" />

      {/* Navigation */}
      <nav className="nav">
        <div className="nav-logo">Woleh</div>
        <div className="nav-links">
          <a href="#expertise">Expertise</a>
          <a href="#portfolio">Work</a>
          <button
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            <div className="toggle-track">
              <div className="toggle-atmosphere" />
              <div className="toggle-sky" />
              <div className="toggle-stars" />
            </div>
            <div className="toggle-celestial toggle-moon">
              <div className="toggle-rays" />
            </div>
          </button>
          <a href="#contact" className="nav-cta">Get in Touch</a>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="hero">
        <div className="hero-content">
          <div className="hero-label">Software Innovation Studio</div>
          <h1 className="hero-title">
            <span className="title-line">Crafting</span>
            <span className="title-line title-accent">Niche</span>
            <span className="title-line">Software</span>
          </h1>
          <p className="hero-description">
            We build exceptional digital products that stand apart.
            From concept to deployment, we transform ambitious ideas into elegant,
            production-ready solutions.
          </p>
          <div className="hero-cta">
            <a href="#portfolio" className="btn btn-primary">View Our Work</a>
            <a href="#expertise" className="btn btn-secondary">Our Expertise</a>
          </div>
        </div>
        <div className="hero-visual">
          <div className="hero-shape hero-shape-1" />
          <div className="hero-shape hero-shape-2" />
          <div className="hero-shape hero-shape-3" />
        </div>
      </header>

      {/* Expertise Section */}
      <section id="expertise" className="expertise">
        <div className="section-header">
          <span className="section-label">What We Do</span>
          <h2 className="section-title">Our Expertise</h2>
          <p className="section-subtitle">
            Deep technical knowledge meets refined craft
          </p>
        </div>
        <div className="services-grid">
          {services.map((service, index) => (
            <article
              key={index}
              className="service-card"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="service-icon">{service.icon}</div>
              <h3 className="service-title">{service.title}</h3>
              <p className="service-description">{service.description}</p>
              <div className="service-line" />
            </article>
          ))}
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="portfolio">
        <div className="section-header">
          <span className="section-label">Selected Work</span>
          <h2 className="section-title">Our Portfolio</h2>
          <p className="section-subtitle">
            Products we've brought to life
          </p>
        </div>
        <div className="portfolio-showcase">
          <article className="portfolio-card portfolio-featured">
            <div className="portfolio-card-inner">
              <div className="portfolio-badge">Featured Product</div>
              <div className="portfolio-logo-wrapper">
                <img
                  src="/kenkosnap_logo.png"
                  alt="KenKoSnap"
                  className="portfolio-logo"
                />
              </div>
              <h3 className="portfolio-title">KenkoSnap</h3>
              <p className="portfolio-description">
                The ultimate AI-powered companion for effortless nutrition tracking
                and body composition analysis. Snap, track, transform.
              </p>
              <div className="portfolio-meta">
                <span className="portfolio-tag">iOS</span>
                <span className="portfolio-tag">AI/ML</span>
                <span className="portfolio-tag">Health Tech</span>
              </div>
              <a
                href="https://kenkosnap.com"
                target="_blank"
                rel="noopener noreferrer"
                className="portfolio-link"
              >
                <span>Visit KenkoSnap</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M7 17L17 7M17 7H7M17 7V17"/>
                </svg>
              </a>
            </div>
            <div className="portfolio-card-glow" />
          </article>

          <article className="portfolio-card portfolio-featured" style={{ animationDelay: '0.15s' }}>
            <div className="portfolio-card-inner">
              <div className="portfolio-badge">Featured Product</div>
              <div className="portfolio-logo-wrapper">
                <img
                  src="/heyalphas_logo.png"
                  alt="HeyAlphas"
                  className="portfolio-logo"
                />
              </div>
              <h3 className="portfolio-title">HeyAlphas</h3>
              <p className="portfolio-description">
                Discover alpha opportunities in cryptocurrency markets with AI-powered
                analysis. Track graduated coins, explore prediction markets, and make
                data-driven investment decisions.
              </p>
              <div className="portfolio-meta">
                <span className="portfolio-tag">iOS</span>
                <span className="portfolio-tag">Android</span>
                <span className="portfolio-tag">AI</span>
                <span className="portfolio-tag">Crypto</span>
              </div>
              <a
                href="https://heyalphas.com"
                target="_blank"
                rel="noopener noreferrer"
                className="portfolio-link"
              >
                <span>Visit HeyAlphas</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M7 17L17 7M17 7H7M17 7V17"/>
                </svg>
              </a>
            </div>
            <div className="portfolio-card-glow" />
          </article>

          <article className="portfolio-card portfolio-upcoming" style={{ animationDelay: '0.3s' }}>
            <div className="portfolio-card-inner">
              <div className="portfolio-badge portfolio-badge-upcoming">Coming Soon</div>
              <div className="portfolio-logo-wrapper">
                <img
                  src="/realornot_logo.png"
                  alt="RealOrNot.ai"
                  className="portfolio-logo"
                />
              </div>
              <h3 className="portfolio-title">RealOrNot.ai</h3>
              <p className="portfolio-description">
                AI-powered deepfake detection and content verification platform.
                Authenticate images, video, and audio against synthetic manipulation
                in milliseconds.
              </p>
              <div className="portfolio-meta">
                <span className="portfolio-tag">Web</span>
                <span className="portfolio-tag">AI</span>
                <span className="portfolio-tag">API</span>
                <span className="portfolio-tag">Security</span>
              </div>
              <a
                href="https://realornot.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="portfolio-link"
              >
                <span>Learn More</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M7 17L17 7M17 7H7M17 7V17"/>
                </svg>
              </a>
            </div>
            <div className="portfolio-card-glow" />
          </article>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="contact-content">
          <span className="section-label">Let's Create</span>
          <h2 className="contact-title">Have a project in mind?</h2>
          <p className="contact-description">
            We're always interested in hearing about new projects and partnerships.
          </p>
          <a href="mailto:info@woleh.com" className="contact-email">
            info@woleh.com
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <div className="footer-logo">Woleh</div>
            <p className="footer-tagline">Innovate. Create. Elevate.</p>
          </div>
          <div className="footer-bottom">
            <p className="footer-copyright">
              &copy; {new Date().getFullYear()} Woleh Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
